/**
 * Connectivity Check page.
 * Verifies Layer 2 reachability between two interfaces (FR-3.3.4).
 */

import { FC, useState, useCallback, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Grid,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  TextField,
  Autocomplete,
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import type {
  InterfaceSummary,
  ReachabilityRequest,
  ReachabilityResponse,
  VLAN,
  GraphData,
} from '@/types/entities';
import api from '@/services/api';
import { GraphVisualization } from '@/components/Graph/GraphVisualization';
import { GraphControls } from '@/components/Graph/GraphControls';
import type { StylesheetStyle } from 'cytoscape';

/**
 * Custom Cytoscape styles for connectivity check visualization.
 * Makes VLAN nodes larger with proper text wrapping to display full VLAN ID and name.
 */
const connectivityGraphStyles: StylesheetStyle[] = [
  // Default node style
  {
    selector: 'node',
    style: {
      'background-color': '#666',
      'label': 'data(label)',
      'text-valign': 'bottom',
      'text-halign': 'center',
      'text-margin-y': 5,
      'font-size': '12px',
      'color': '#333',
      'text-wrap': 'wrap',
      'text-max-width': '100px',
      'text-outline-color': '#fff',
      'text-outline-width': 2,
      'overlay-opacity': 0,
    },
  },
  // Interface nodes - green ellipses (matches detail views)
  {
    selector: 'node[type="interface"]',
    style: {
      'shape': 'ellipse',
      'background-color': '#2e7d32',
      'width': 60,
      'height': 60,
      'font-size': '11px',
      'text-wrap': 'wrap',
      'text-max-width': '100px',
    },
  },
  // VLAN nodes - orange rounded rectangles (matches detail views)
  {
    selector: 'node[type="vlan"]',
    style: {
      'shape': 'roundrectangle',
      'background-color': '#ed6c02',
      'width': 70,
      'height': 70,
      'font-size': '11px',
      'text-wrap': 'wrap',
      'text-max-width': '100px',
    },
  },
  // Switch nodes - blue circles (matches detail views)
  {
    selector: 'node[type="switch"]',
    style: {
      'shape': 'ellipse',
      'background-color': '#1976d2',
      'width': 60,
      'height': 60,
      'font-size': '14px',
      'font-weight': 'bold',
    },
  },
  // Edges
  {
    selector: 'edge',
    style: {
      'width': 3,
      'line-color': '#ccc',
      'target-arrow-color': '#ccc',
      'target-arrow-shape': 'triangle',
      'curve-style': 'bezier',
    },
  },
];

/**
 * Transform ReachabilityResponse to GraphData format for visualization
 */
function transformReachabilityToGraphData(
  response: ReachabilityResponse,
  interface1: InterfaceSummary | null,
  interface2: InterfaceSummary | null
): GraphData {
  const interface1Data = response.path?.interface1 || interface1 || { uniqueId: 'interface1', name: 'Interface 1', mode: 'access' as const, switchHostname: '' };
  const interface2Data = response.path?.interface2 || interface2 || { uniqueId: 'interface2', name: 'Interface 2', mode: 'access' as const, switchHostname: '' };

  // Extract switch hostnames from interface data
  const switch1Hostname = interface1Data.switchHostname || interface1Data.uniqueId.split('-').slice(0, -1).join('-');
  const switch2Hostname = interface2Data.switchHostname || interface2Data.uniqueId.split('-').slice(0, -1).join('-');

  const nodes = [
    // Switch 1 node
    {
      id: `switch-${switch1Hostname}`,
      label: switch1Hostname,
      type: 'switch' as const,
      data: { hostname: switch1Hostname },
    },
    // Interface 1 node
    {
      id: 'interface1',
      label: `${interface1Data.name}\n(${switch1Hostname})`,
      type: 'interface' as const,
      data: interface1Data,
    },
    // VLAN nodes
    ...response.sharedVlans.map((vlan) => ({
      id: `vlan-${vlan.vlanID}`,
      label: `VLAN ${vlan.vlanID}${vlan.name ? `\n${vlan.name}` : ''}`,
      type: 'vlan' as const,
      data: vlan,
    })),
    // Interface 2 node
    {
      id: 'interface2',
      label: `${interface2Data.name}\n(${switch2Hostname})`,
      type: 'interface' as const,
      data: interface2Data,
    },
    // Switch 2 node
    {
      id: `switch-${switch2Hostname}`,
      label: switch2Hostname,
      type: 'switch' as const,
      data: { hostname: switch2Hostname },
    },
  ];

  const edges = [
    // Switch 1 -> Interface 1
    {
      id: 'edge-switch1-interface1',
      source: `switch-${switch1Hostname}`,
      target: 'interface1',
      type: 'HAS_INTERFACE' as const,
      label: '',
      data: {},
    },
    // Interface 1 -> VLANs
    ...response.sharedVlans.map((vlan) => ({
      id: `edge-interface1-${vlan.vlanID}`,
      source: 'interface1',
      target: `vlan-${vlan.vlanID}`,
      type: 'MEMBER_OF' as const,
      label: '',
      data: {},
    })),
    // VLANs -> Interface 2
    ...response.sharedVlans.map((vlan) => ({
      id: `edge-${vlan.vlanID}-interface2`,
      source: `vlan-${vlan.vlanID}`,
      target: 'interface2',
      type: 'MEMBER_OF' as const,
      label: '',
      data: {},
    })),
    // Interface 2 -> Switch 2
    {
      id: 'edge-interface2-switch2',
      source: 'interface2',
      target: `switch-${switch2Hostname}`,
      type: 'HAS_INTERFACE' as const,
      label: '',
      data: {},
    },
  ];

  return { nodes, edges };
}

export const ConnectivityCheck: FC = () => {
  // State for interface selection
  const [selectedInterface1, setSelectedInterface1] = useState<InterfaceSummary | null>(null);
  const [selectedInterface2, setSelectedInterface2] = useState<InterfaceSummary | null>(null);
  const [interfaces, setInterfaces] = useState<InterfaceSummary[]>([]);
  const [interfacesLoading, setInterfacesLoading] = useState<boolean>(false);

  // State for reachability check
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [results, setResults] = useState<ReachabilityResponse | null>(null);

  // Graph container ref for controls
  const graphContainerRef = useRef<HTMLDivElement>(null);

  /**
   * Load all interfaces on component mount
   */
  useEffect(() => {
    const loadInterfaces = async () => {
      setInterfacesLoading(true);
      try {
        const response = await api.getInterfaces();
        setInterfaces(response.interfaces);
      } catch (err) {
        console.error('Failed to load interfaces:', err);
        // Don't show error for interface list loading - user can still type interface IDs
      } finally {
        setInterfacesLoading(false);
      }
    };

    loadInterfaces();
  }, []);

  /**
   * Handle interface 1 selection
   */
  const handleInterface1Change = useCallback(
    (_event: React.SyntheticEvent, value: string | InterfaceSummary | null) => {
      if (typeof value === 'string') {
        const trimmed = value.trim();
        if (trimmed) {
          const found = interfaces.find((i) => i.uniqueId === trimmed);
          setSelectedInterface1(
            found || {
              uniqueId: trimmed,
              name: trimmed,
              switchHostname: '',
            }
          );
        } else {
          setSelectedInterface1(null);
        }
      } else {
        setSelectedInterface1(value);
      }
    },
    [interfaces]
  );

  /**
   * Handle interface 2 selection
   */
  const handleInterface2Change = useCallback(
    (_event: React.SyntheticEvent, value: string | InterfaceSummary | null) => {
      if (typeof value === 'string') {
        const trimmed = value.trim();
        if (trimmed) {
          const found = interfaces.find((i) => i.uniqueId === trimmed);
          setSelectedInterface2(
            found || {
              uniqueId: trimmed,
              name: trimmed,
              switchHostname: '',
            }
          );
        } else {
          setSelectedInterface2(null);
        }
      } else {
        setSelectedInterface2(value);
      }
    },
    [interfaces]
  );


  /**
   * Handle connectivity check
   */
  const handleCheck = useCallback(async () => {
    if (!selectedInterface1 || !selectedInterface2) {
      setError('Please select both interfaces');
      return;
    }

    setLoading(true);
    setError(null);
    setResults(null);

    try {
      const request: ReachabilityRequest = {
        interface1: selectedInterface1.uniqueId,
        interface2: selectedInterface2.uniqueId,
      };

      const data = await api.checkReachability(request);
      setResults(data);
      setError(null);
    } catch (err) {
      if (err instanceof Error) {
        setError(`Failed to check connectivity: ${err.message}`);
      } else {
        setError('Failed to check connectivity. Please ensure both interfaces exist.');
      }
      setResults(null);
    } finally {
      setLoading(false);
    }
  }, [selectedInterface1, selectedInterface2]);

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Connectivity Check
      </Typography>

      {/* Input Card */}
      <Card sx={{ mt: 2 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Check Layer 2 Reachability
          </Typography>

          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={5}>
              <Autocomplete
                options={interfaces}
                value={selectedInterface1}
                onChange={handleInterface1Change}
                groupBy={(option) => option.switchHostname}
                getOptionLabel={(option) => {
                  if (typeof option === 'string') return option;
                  const switchName = option.switchHostname || '';
                  const mode = option.mode ? ` (${option.mode})` : '';
                  const status = option.status ? ` - ${option.status}` : '';
                  return `${switchName}/${option.name}${mode}${status}`;
                }}
                renderOption={(props, option) => (
                  <Box component="li" {...props}>
                    <Box>
                      <Typography variant="body2">{option.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {option.uniqueId}
                        {option.mode && ` • ${option.mode}`}
                        {option.status && ` • ${option.status}`}
                      </Typography>
                    </Box>
                  </Box>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Interface 1"
                    placeholder="Select or type interface ID..."
                    size="small"
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
                          {params.InputProps.startAdornment}
                        </>
                      ),
                      endAdornment: (
                        <>
                          {interfacesLoading ? <CircularProgress size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                loading={interfacesLoading}
                disabled={loading}
                freeSolo
              />
            </Grid>

            <Grid item xs={12} md={5}>
              <Autocomplete
                options={interfaces}
                value={selectedInterface2}
                onChange={handleInterface2Change}
                groupBy={(option) => option.switchHostname}
                getOptionLabel={(option) => {
                  if (typeof option === 'string') return option;
                  const switchName = option.switchHostname || '';
                  const mode = option.mode ? ` (${option.mode})` : '';
                  const status = option.status ? ` - ${option.status}` : '';
                  return `${switchName}/${option.name}${mode}${status}`;
                }}
                renderOption={(props, option) => (
                  <Box component="li" {...props}>
                    <Box>
                      <Typography variant="body2">{option.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {option.uniqueId}
                        {option.mode && ` • ${option.mode}`}
                        {option.status && ` • ${option.status}`}
                      </Typography>
                    </Box>
                  </Box>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Interface 2"
                    placeholder="Select or type interface ID..."
                    size="small"
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
                          {params.InputProps.startAdornment}
                        </>
                      ),
                      endAdornment: (
                        <>
                          {interfacesLoading ? <CircularProgress size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                loading={interfacesLoading}
                disabled={loading}
                freeSolo
              />
            </Grid>

            <Grid item xs={12} md={2}>
              <Button
                fullWidth
                variant="contained"
                startIcon={loading ? <CircularProgress size={16} /> : <CheckIcon />}
                onClick={handleCheck}
                disabled={loading || !selectedInterface1 || !selectedInterface2}
                sx={{ height: '40px' }}
              >
                Check
              </Button>
            </Grid>
          </Grid>

          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Select or type two interface IDs to check if they can communicate at Layer 2 (share
            a VLAN).
          </Typography>
        </CardContent>
      </Card>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}

      {/* Results Card */}
      {results && (
        <Card sx={{ mt: 2 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Typography variant="h6">Results</Typography>
              {results.reachable ? (
                <Chip
                  icon={<CheckIcon />}
                  label="Reachable"
                  color="success"
                  size="small"
                />
              ) : (
                <Chip icon={<CancelIcon />} label="Not Reachable" color="error" size="small" />
              )}
            </Box>

            {results.reachable ? (
              <>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  The two interfaces can communicate at Layer 2 through the following shared
                  VLANs:
                </Typography>

                <TableContainer component={Paper} variant="outlined" sx={{ mt: 2 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>VLAN ID</TableCell>
                        <TableCell>Name</TableCell>
                        <TableCell>Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {results.sharedVlans.map((vlan: VLAN) => (
                        <TableRow key={vlan.vlanID}>
                          <TableCell>{vlan.vlanID}</TableCell>
                          <TableCell>{vlan.name || 'N/A'}</TableCell>
                          <TableCell>
                            <Chip
                              label={vlan.status || 'unknown'}
                              size="small"
                              color={vlan.status === 'active' ? 'success' : 'default'}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            ) : (
              <Typography variant="body2" color="text.secondary">
                The two interfaces do not share any VLANs and cannot communicate at Layer 2.
              </Typography>
            )}
          </CardContent>
        </Card>
      )}

      {/* Path Visualization Card */}
      {results && results.reachable && (
        <Card sx={{ mt: 2 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Path Visualization
            </Typography>
            <Box sx={{ position: 'relative' }}>
              <GraphVisualization
                ref={graphContainerRef}
                data={transformReachabilityToGraphData(
                  results,
                  selectedInterface1,
                  selectedInterface2
                )}
                height="400px"
                layout="breadthfirst"
                customStyles={connectivityGraphStyles}
              />
              <GraphControls containerRef={graphContainerRef} />
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              Blue circles: Switches • Green circles: Interfaces • Orange rounded rectangles: VLANs
            </Typography>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};
